import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

/**
 * ТЗ 0088 — кто видит раздел «Маркетинг». Зеркало `MARKETING_READ_ROLES`
 * бэкенда: Admin и новая роль Marketing (маркетолог, агентство). Продавцы,
 * финансы, HR и методист раздела не получают — ни пункта меню, ни маршрута,
 * ни команды палитры, ни запроса.
 *
 * Меню, маршрут и палитра берут список ТОЛЬКО отсюда — три места не имеют
 * права разойтись. Скрытие — UX, авторитет у бэкенда.
 */
export const MARKETING_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.MARKETING];

/**
 * Правка справочников (правила каналов, семейства и алиасы, офферы, тип
 * воронки Kommo) и кнопка «Создать правило» на вкладке качества. Зеркало
 * `MARKETING_EDIT_ROLES`.
 */
export const MARKETING_EDIT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.MARKETING,
];

/**
 * Только Admin: «Обновить сейчас» у источников, ручная правка продажи и
 * адреса покупателей без маски.
 */
export const MARKETING_ADMIN_ROLES: UserRole[] = [UserRole.ADMIN];

export interface IMarketingPermissions {
  /** Раздел целиком: все вкладки на чтение. */
  canUse: boolean;
  /** Справочники и «Создать правило». */
  canEdit: boolean;
  /** «Обновить сейчас», правка продажи, email без маски. */
  isAdmin: boolean;
}

const hasAny = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/** Роли без React — для чистых проверок и тестов. */
export const marketingPermissionsFor = (
  roles: UserRole[],
): IMarketingPermissions => ({
  canUse: hasAny(roles, MARKETING_ROLES),
  canEdit: hasAny(roles, MARKETING_EDIT_ROLES),
  isAdmin: hasAny(roles, MARKETING_ADMIN_ROLES),
});

export const useMarketingPermissions = (): IMarketingPermissions => {
  const userState = useReactiveVar(userStateVar);
  return marketingPermissionsFor((userState.me?.roles ?? []) as UserRole[]);
};
